import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Home, Calendar, CheckSquare, FileText, Users, LogOut,
  StickyNote, Wallet, Bell, ChevronRight,
} from 'lucide-react';
import Avatar from './Avatar';

// Sidebar rediseñado — matches mockup 2026-02-28:
// - V&O logo + "VIDA EN ORDEN / MENOS OLVIDOS"
// - User avatar card + "Ver mi perfil ›"
// - 8 items nav (Inicio, Tareas, Calendario, Avisos, Documentos, Gastos, Recordatorios, Familia)
// - Cerrar sesión al pie
export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // isActive custom para diferenciar Calendario (sin tab param) del resto.
  const calendarIsActive = () =>
    location.pathname === '/dashboard/calendar'
    && new URLSearchParams(location.search).get('tab') !== 'reminders';

  const navItems = [
    { path: '/dashboard', icon: Home, label: 'Inicio', testId: 'nav-home' },
    { path: '/dashboard/tasks', icon: CheckSquare, label: 'Tareas', testId: 'nav-tasks' },
    { path: '/dashboard/calendar', icon: Calendar, label: 'Calendario', testId: 'nav-calendar', isActive: calendarIsActive },
    { path: '/dashboard/announcements', icon: StickyNote, label: 'Avisos', testId: 'nav-announcements' },
    { path: '/dashboard/documents', icon: FileText, label: 'Documentos', testId: 'nav-documents' },
    { path: '/dashboard/expenses', icon: Wallet, label: 'Gastos', testId: 'nav-expenses' },
    { path: '/dashboard/reminders', icon: Bell, label: 'Recordatorios', testId: 'nav-reminders' },
    { path: '/dashboard/family', icon: Users, label: 'Familia', testId: 'nav-family' },
  ];

  return (
    <div className="h-full flex flex-col px-5 py-6 overflow-y-auto">
      {/* Logo V&O + subtitle */}
      <div className="mb-5 px-1">
        <h1 className="text-4xl font-display font-extrabold tracking-tight leading-none">
          <span className="text-slate-900 dark:text-white">V</span>
          <span className="text-[#2563EB] dark:text-[#3B82F6]">&amp;</span>
          <span className="text-slate-900 dark:text-white">O</span>
        </h1>
        <p className="text-[10px] font-bold tracking-[0.28em] text-slate-500 dark:text-slate-400 mt-1.5 leading-tight">
          VIDA EN ORDEN
        </p>
        <p className="text-[9px] font-semibold tracking-[0.26em] text-slate-400 dark:text-slate-500 mt-0.5 leading-tight">
          MENOS OLVIDOS
        </p>
      </div>

      {/* User card — avatar + nombre + Ver mi perfil */}
      <button
        onClick={() => navigate('/dashboard/profile')}
        data-testid="sidebar-user-card"
        className="mb-6 flex items-center gap-3 rounded-2xl p-2 pr-3 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors text-left"
      >
        <Avatar name={user?.name} picture={user?.picture} size="sm" testId="sidebar-avatar" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900 dark:text-white truncate leading-tight">
            {user?.name || 'Mi cuenta'}
          </p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-0.5">
            Ver mi perfil <ChevronRight className="w-3 h-3" strokeWidth={2.5} />
          </p>
        </div>
      </button>

      {/* Nav */}
      <nav className="flex-1 space-y-1">
        {navItems.map((item) => {
          const customActive = item.isActive ? item.isActive() : null;
          return (
            <NavLink
              key={item.testId}
              to={item.path}
              data-testid={item.testId}
              end={item.path === '/dashboard'}
              className={({ isActive }) => {
                const active = customActive !== null ? customActive : isActive;
                return [
                  'flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-all duration-200 relative group',
                  active
                    ? 'bg-[#2563EB] text-white font-semibold shadow-lg shadow-[#2563EB]/25 dark:bg-[#2563EB] dark:text-white'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white font-medium',
                ].join(' ');
              }}
            >
              <item.icon className="w-[18px] h-[18px]" strokeWidth={2} />
              <span className="text-[14px]">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Cerrar sesión */}
      <button
        data-testid="logout-btn"
        onClick={handleLogout}
        className="mt-4 flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors w-full text-sm font-semibold"
      >
        <LogOut className="w-[18px] h-[18px]" strokeWidth={2} />
        <span>Cerrar sesión</span>
      </button>
    </div>
  );
}
